import {
	ApplicationInsights,
	type ITelemetryItem,
} from '@microsoft/applicationinsights-web';
import { parseCookie } from 'next/dist/compiled/@edge-runtime/cookies';

import { publicRuntimeConfig } from 'config';
import { is, isClient } from 'utils/helpers';

function filterException(item: ITelemetryItem): boolean {
	if (!is.object(item.data)) {
		return true;
	}
	if (
		'errorSrc' in item.data &&
		is.string(item.data.errorSrc) &&
		!item.data?.errorSrc?.includes('_next/static')
	) {
		return false;
	}
	// Ignore minified React error #421 since it's probably from sitecore jss
	// try uningnoring after upgrading to version that fixes suspense errors
	// eslint-disable-next-line sonarjs/prefer-single-boolean-return
	if (
		'message' in item.data &&
		is.string(item.data.message) &&
		item.data?.message?.includes('Minified React error #421')
	) {
		return false;
	}

	return true;
}

const includedTargetDomain =
	/^(?:https?:\/\/)?(?:(?:www|apigw)[-\w]*?\.)?jula(?:dev)?\./;

function filterDependency(item: ITelemetryItem): boolean {
	if (!is.object(item.baseData)) {
		return true;
	}

	if (
		'responseCode' in item.baseData &&
		is.number(item.baseData.responseCode) &&
		item.baseData.responseCode === 0
	) {
		return false;
	}

	if ('target' in item.baseData && is.string(item.baseData.target)) {
		const target = item.baseData.target ?? '';
		return includedTargetDomain.test(target) && !target.includes('cdn-cgi/rum');
	}
	return true;
}

function filterTelemetry(item: ITelemetryItem): boolean {
	if (item?.baseType === 'ExceptionData') {
		return filterException(item);
	}
	if (item?.baseType === 'RemoteDependencyData') {
		return filterDependency(item);
	}
	return true;
}

export default function initApplicationInsights() {
	if (
		!isClient() ||
		!publicRuntimeConfig?.NEXT_PUBLIC_APPINSIGHTS_CONNECTIONSTRING ||
		parseCookie(document.cookie).get('disableAI') === 'true'
	) {
		return;
	}
	const appInsights = new ApplicationInsights({
		config: {
			samplingPercentage: Number.parseInt(
				publicRuntimeConfig?.NEXT_PUBLIC_NEXT_CLIENT_INSTRUMENTATION_SAMPLING_RATE ||
					'100',
				10,
			),
			connectionString:
				publicRuntimeConfig?.NEXT_PUBLIC_APPINSIGHTS_CONNECTIONSTRING,
			enableCorsCorrelation: true,
			correlationHeaderDomains:
				publicRuntimeConfig?.NEXT_PUBLIC_INSTRUMENTATION_CORRELATION_HEADER_DOMAINS?.split(
					',',
				).map((d) => d.trim()),
			enableAutoRouteTracking: true,
		},
	});
	appInsights.loadAppInsights();
	appInsights.appInsights.trackPageView();
	appInsights.addTelemetryInitializer(filterTelemetry);
}
